import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getAdminListApi } from 'src/services/api';

export const ADMIN_DATA_CHANGED_EVENT = 'admin-data-changed';

interface AdminContextValue {
  isAdmin: boolean;
  isSuperAdmin: boolean;
  adminUserIds: number[];
  superAdminUserId: number | null;
  loading: boolean;
  refreshAdminList: () => Promise<void>;
  refreshAdminData: () => Promise<void>;
}

const AdminContext = createContext<AdminContextValue | null>(null);

const STORAGE_USER = 'flower_power_notes_user';

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [adminUserIds, setAdminUserIds] = useState<number[]>([]);
  const [superAdminUserId, setSuperAdminUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [apiDenied, setApiDenied] = useState(false);

  const refreshAdminList = useCallback(async () => {
    const raw = localStorage.getItem(STORAGE_USER);
    if (!raw) {
      setAdminUserIds([]);
      setSuperAdminUserId(null);
      setApiDenied(false);
      setLoading(false);
      return;
    }
    try {
      const user = JSON.parse(raw);
      const authToken = user?.AuthToken;
      const userId = user?.UserId != null ? Number(user.UserId) : null;
      if (!authToken || userId == null) {
        setAdminUserIds([]);
        setSuperAdminUserId(null);
        setApiDenied(false);
        setLoading(false);
        return;
      }
      const result = await getAdminListApi(authToken);
      if (result.error) {
        setAdminUserIds([]);
        setSuperAdminUserId(null);
        setApiDenied(true);
      } else {
        setAdminUserIds(result.adminUserIds ?? []);
        setSuperAdminUserId(result.superAdminUserId ?? null);
        setApiDenied(false);
      }
    } catch {
      setAdminUserIds([]);
      setSuperAdminUserId(null);
      setApiDenied(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshAdminData = useCallback(async () => {
    await refreshAdminList();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(ADMIN_DATA_CHANGED_EVENT));
    }
  }, [refreshAdminList]);

  useEffect(() => {
    refreshAdminList();
  }, [refreshAdminList]);

  const value = useMemo(() => {
    if (loading) {
      return {
        isAdmin: false,
        isSuperAdmin: false,
        adminUserIds,
        superAdminUserId,
        loading,
        refreshAdminList,
        refreshAdminData,
      };
    }
    if (apiDenied) {
      return {
        isAdmin: false,
        isSuperAdmin: false,
        adminUserIds,
        superAdminUserId,
        loading,
        refreshAdminList,
        refreshAdminData,
      };
    }
    const raw = localStorage.getItem(STORAGE_USER);
    const user = raw ? JSON.parse(raw) : {};
    const userId = user?.UserId != null ? Number(user.UserId) : null;
    const isSuperAdmin = userId != null && superAdminUserId != null && userId === superAdminUserId;
    const isAdmin = userId != null && (isSuperAdmin || adminUserIds.includes(userId));
    return {
      isAdmin,
      isSuperAdmin,
      adminUserIds,
      superAdminUserId,
      loading,
      refreshAdminList,
      refreshAdminData,
    };
  }, [adminUserIds, superAdminUserId, loading, apiDenied, refreshAdminList, refreshAdminData]);

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

export const AdminRefreshOnAuth: React.FC<{ isLoggedIn: boolean; children: React.ReactNode }> = ({ isLoggedIn, children }) => {
  const { refreshAdminList } = useAdmin();
  useEffect(() => {
    if (isLoggedIn) refreshAdminList();
  }, [isLoggedIn, refreshAdminList]);
  return <>{children}</>;
};

export function useAdmin(): AdminContextValue {
  const ctx = useContext(AdminContext);
  if (!ctx) {
    return {
      isAdmin: false,
      isSuperAdmin: false,
      adminUserIds: [],
      superAdminUserId: null,
      loading: false,
      refreshAdminList: async () => {},
      refreshAdminData: async () => {},
    };
  }
  return ctx;
}
